import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { Attachment } from '@medicail/shared';
import { useConfig } from '@/lib/config';
import { errorMessage } from '@/lib/http';
import { isImageMime, resolveMimeType, uploadFile, validateFiles } from '@/lib/uploads';
import { rememberPreview } from '@/lib/previewCache';

export interface PendingAttachment {
  localId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  kind: 'image' | 'document';
  status: 'uploading' | 'done' | 'error';
  progress: number;
  attachmentId?: string;
  previewUrl?: string;
  error?: string;
}

let counter = 0;

export function useAttachments() {
  const { uploads } = useConfig();
  const [items, setItems] = useState<PendingAttachment[]>([]);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const controllers = useRef(new Map<string, AbortController>());

  const patch = useCallback((localId: string, p: Partial<PendingAttachment>) => {
    setItems((prev) => prev.map((i) => (i.localId === localId ? { ...i, ...p } : i)));
  }, []);

  const addFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      const { accepted, errors } = validateFiles(files, itemsRef.current.length, uploads);
      errors.forEach((e) => toast.error(e));
      for (const file of accepted) {
        const mimeType = resolveMimeType(file);
        const localId = `att-${++counter}`;
        const kind = isImageMime(mimeType) ? 'image' : 'document';
        const previewUrl = kind === 'image' ? URL.createObjectURL(file) : undefined;
        const controller = new AbortController();
        controllers.current.set(localId, controller);
        setItems((prev) => [
          ...prev,
          { localId, fileName: file.name, mimeType, sizeBytes: file.size, kind, status: 'uploading', progress: 0, previewUrl },
        ]);
        uploadFile(file, { signal: controller.signal, onProgress: (f) => patch(localId, { progress: f }) })
          .then(({ attachmentId }) => {
            if (previewUrl) rememberPreview(attachmentId, previewUrl);
            patch(localId, { status: 'done', progress: 1, attachmentId });
          })
          .catch((err) => {
            if (controller.signal.aborted) return;
            patch(localId, { status: 'error', error: errorMessage(err, 'Upload failed') });
            toast.error(`Could not upload "${file.name}". ${errorMessage(err, '')}`.trim());
          })
          .finally(() => controllers.current.delete(localId));
      }
    },
    [uploads, patch],
  );

  const remove = useCallback((localId: string) => {
    controllers.current.get(localId)?.abort();
    controllers.current.delete(localId);
    setItems((prev) => {
      const item = prev.find((i) => i.localId === localId);
      if (item?.previewUrl && !item.attachmentId) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((i) => i.localId !== localId);
    });
  }, []);

  /** Take the finished uploads for sending and reset the tray. */
  const consume = useCallback((): Attachment[] => {
    const done = itemsRef.current
      .filter((i) => i.status === 'done' && i.attachmentId)
      .map<Attachment>((i) => ({
        attachmentId: i.attachmentId!,
        fileName: i.fileName,
        mimeType: i.mimeType,
        sizeBytes: i.sizeBytes,
        kind: i.kind,
      }));
    setItems([]);
    return done;
  }, []);

  useEffect(() => {
    const map = controllers.current;
    return () => map.forEach((c) => c.abort());
  }, []);

  return {
    items,
    addFiles,
    remove,
    consume,
    uploading: items.some((i) => i.status === 'uploading'),
    hasErrors: items.some((i) => i.status === 'error'),
    count: items.length,
    maxFiles: uploads.maxFilesPerMessage,
    accept: uploads.acceptedMimeTypes.join(','),
  };
}
